"use client";

import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createTransfer, getTransfers, reset } from "@/state/transfers/transfer.slice";
import { getProducts } from "@/state/products/product.slice";
import { getShops } from "@/state/shops/shop.slice";
import { getClientShopScope } from "@/lib/shopScope";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { Loader2, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

export default function TransferCreatePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { isLoading, success, error } = useSelector((state) => state.transfers);
  const { products } = useSelector((state) => state.product);
  const user = useSelector((state) => state.auth.user);
  const { shops } = useSelector((state) => state.shop);

  const shopScope = getClientShopScope(user);
  const sourceShops = shopScope.all
    ? (shops || [])
    : (shops || []).filter((s) => shopScope.ids.includes(Number(s.id)));
  const canChooseSource = shopScope.all || sourceShops.length > 1;

  const [fromShopId, setFromShopId] = useState("");
  const [toShopId, setToShopId] = useState("");
  const [notes, setNotes] = useState("");
  const [transferItems, setTransferItems] = useState([]);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [itemQuantity, setItemQuantity] = useState("");

  useEffect(() => {
    dispatch(getShops());
    dispatch(getProducts({ limit: 1000 }));
  }, [dispatch]);

  // Show the SOURCE shop's available stock in the picker.
  useEffect(() => {
    if (fromShopId) dispatch(getProducts({ limit: 1000, shop: fromShopId }));
  }, [dispatch, fromShopId]);

  // Non-privileged users transfer out of their own shop — auto-detect it.
  useEffect(() => {
    if (shopScope.all || fromShopId) return;
    const inScope = user?.shop_id && shopScope.ids.includes(Number(user.shop_id));
    const preferred = inScope
      ? String(user.shop_id)
      : (sourceShops[0] ? String(sourceShops[0].id) : (shopScope.ids[0] ? String(shopScope.ids[0]) : ""));
    if (preferred) setFromShopId(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopScope.all, user, shops]);

  // On success, return to the list.
  useEffect(() => {
    if (success) {
      toast({ title: "Success", description: "Transfer initiated — pending receipt." });
      dispatch(reset());
      dispatch(getTransfers({ page: 1, limit: 20, search: "" }));
      navigate("/dashboard/inventory/transfers");
    }
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error });
      dispatch(reset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, error]);

  const filteredProducts = (products || []).filter((p) =>
    p.title?.toLowerCase().includes(productSearchTerm.toLowerCase()) &&
    (!fromShopId || (p.stockQuantity ?? 0) > 0)
  );

  const handleAddItem = useCallback(() => {
    if (!selectedProductId || !itemQuantity || parseInt(itemQuantity) <= 0) {
      toast({ variant: "destructive", title: "Error", description: "Select a product and enter a valid quantity" });
      return;
    }
    if (transferItems.some((i) => i.product_id === selectedProductId)) {
      toast({ variant: "destructive", title: "Error", description: "Product already added" });
      return;
    }
    const product = products?.find((p) => p.id === selectedProductId);
    setTransferItems((prev) => [
      ...prev,
      { product_id: selectedProductId, product_title: product?.title || `Product #${selectedProductId}`, quantity: parseInt(itemQuantity) },
    ]);
    setSelectedProductId(null);
    setItemQuantity("");
  }, [selectedProductId, itemQuantity, transferItems, products, toast]);

  const handleRemoveItem = useCallback((productId) => {
    setTransferItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!fromShopId || !toShopId) {
      toast({ variant: "destructive", title: "Error", description: "Select source and destination shops" });
      return;
    }
    if (fromShopId === toShopId) {
      toast({ variant: "destructive", title: "Error", description: "Source and destination must be different" });
      return;
    }
    if (transferItems.length === 0) {
      toast({ variant: "destructive", title: "Error", description: "Add at least one item" });
      return;
    }
    dispatch(createTransfer({
      from_shop_id: parseInt(fromShopId),
      to_shop_id: parseInt(toShopId),
      notes,
      items: transferItems.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
    }));
  }, [dispatch, fromShopId, toShopId, notes, transferItems, toast]);

  return (
    <div className="mx-auto max-w-3xl p-4 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate("/dashboard/inventory/transfers")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">New Stock Transfer</h1>
          <p className="text-sm text-muted-foreground">Move stock from one shop to another (pending until received).</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Shops</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From Shop *</Label>
              {canChooseSource ? (
                <Select value={fromShopId} onValueChange={setFromShopId}>
                  <SelectTrigger><SelectValue placeholder="Source shop" /></SelectTrigger>
                  <SelectContent>
                    {sourceShops.map((shop) => (
                      <SelectItem key={shop.id} value={String(shop.id)}>{shop.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="h-10 px-3 flex items-center rounded-md border bg-muted text-sm text-muted-foreground">
                  {(shops || []).find((s) => String(s.id) === fromShopId)?.name || sourceShops[0]?.name || "Your shop"}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>To Shop *</Label>
              <Select value={toShopId} onValueChange={setToShopId}>
                <SelectTrigger><SelectValue placeholder="Destination shop" /></SelectTrigger>
                <SelectContent>
                  {(shops || []).filter((s) => String(s.id) !== fromShopId).map((shop) => (
                    <SelectItem key={shop.id} value={String(shop.id)}>{shop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Products</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="flex-1 justify-between">
                  {selectedProductId
                    ? products?.find((p) => p.id === selectedProductId)?.title || "Select product"
                    : "Select product"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[350px] p-0">
                <Command>
                  <CommandInput placeholder="Search products..." value={productSearchTerm} onValueChange={setProductSearchTerm} />
                  <CommandEmpty>No products with stock at this shop.</CommandEmpty>
                  <CommandGroup className="max-h-[240px] overflow-y-auto">
                    {filteredProducts.slice(0, 50).map((product) => (
                      <CommandItem key={product.id} value={product.title}
                        onSelect={() => { setSelectedProductId(product.id); setProductSearchOpen(false); }}>
                        <Check className={cn("mr-2 h-4 w-4", selectedProductId === product.id ? "opacity-100" : "opacity-0")} />
                        {product.title}
                        <span className="ml-auto text-xs text-muted-foreground">Stock: {product.stockQuantity ?? 0}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
            <Input type="number" placeholder="Qty" value={itemQuantity}
              onChange={(e) => setItemQuantity(e.target.value)} className="w-[100px]" min="1" />
            <Button type="button" onClick={handleAddItem} variant="secondary"><Plus className="h-4 w-4" /></Button>
          </div>

          {transferItems.length > 0 && (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="w-[100px]">Quantity</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transferItems.map((item) => (
                    <TableRow key={item.product_id}>
                      <TableCell className="font-medium">{item.product_title}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveItem(item.product_id)}
                          className="text-red-500 hover:text-red-600 h-8 w-8">
                          <Trash className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any notes about this transfer..." rows={2} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/dashboard/inventory/transfers")}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isLoading || !fromShopId || !toShopId || transferItems.length === 0}>
          {isLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Transferring...</>) : "Complete Transfer"}
        </Button>
      </div>
    </div>
  );
}
